import { Document, Packer } from 'docx';

/**
 * Abstract class representing a base for generating DOCX reports.
 *
 * @template T - The type of data used to generate the report.
 */
export abstract class DocxReport<T> {
  /**
   * The data used to generate the report. Initially set to `null`.
   */
  protected data: T | null = null;

  /**
   * Fetches the data required for generating the report.
   * This method should be implemented in derived classes to populate the `data` property.
   *
   * @returns A promise that resolves when the data has been fetched.
   */
  protected async fetchData(): Promise<void> {}

  /**
   * Creates the DOCX document for the report.
   * This method must be implemented in derived classes to define the document structure.
   *
   * @returns The generated DOCX document.
   */
  protected abstract createDocument(): Document;

  /**
   * Generates the DOCX report and triggers a download of the file.
   *
   * @param filename - The name of the file to be downloaded.
   * @returns A promise that resolves when the report generation and download are complete.
   */
  public async generate(filename: string): Promise<void> {
    await this.fetchData();
    const document = this.createDocument();
    const blob = await Packer.toBlob(document);
    this.downloadFile(blob, filename);
  }

  private downloadFile(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
